import { MutationCache, QueryCache, QueryClient } from '@tanstack/vue-query'

import { toast } from '#ui/sonner'
import { isClientError, messageFrom } from '#utils/errors'

declare module '@tanstack/vue-query' {
  interface Register {
    mutationMeta: {
      success?: string | ((data: unknown, variables: unknown) => string)
      error?: string | ((error: unknown, variables: unknown) => string)
      invalidates?: false | readonly (readonly unknown[])[]
    }
    queryMeta: { noun?: string }
  }
}

type Message<T> = string | ((value: T, variables: unknown) => string) | undefined

function resolve<T>(message: Message<T>, value: T, variables: unknown): string | undefined {
  return typeof message === 'function' ? message(value, variables) : message
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.state.data === undefined) return
      toast.error(messageFrom(error, `Couldn't refresh ${query.meta?.noun ?? 'this page'}.`))
    },
  }),
  mutationCache: new MutationCache({
    onSuccess: (data, variables, _context, mutation) => {
      const invalidates = mutation.meta?.invalidates
      if (invalidates === undefined) {
        queryClient.invalidateQueries()
      } else if (invalidates !== false) {
        for (const queryKey of invalidates) {
          queryClient.invalidateQueries({ queryKey })
        }
      }

      const message = resolve(mutation.meta?.success, data, variables)
      if (message) toast.success(message)
    },
    onError: (error, variables, _context, mutation) => {
      const fallback = resolve(mutation.meta?.error, error, variables)
      toast.error(messageFrom(error, fallback ?? 'Something went wrong.'))
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      retry: (failureCount, error) => failureCount < 3 && !isClientError(error),
    },
  },
})
